import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { DataTable, DataTablePagination } from 'components/common'
import { useQuery } from 'hooks'
import { fetchKdsSyncLogDetail, fetchKdsSyncLogs } from 'utils/api/logsApi'
import { calculateOffset } from 'utils/helpers/pagination'
import { filterEmptyValues, FilterMeta } from 'utils/helpers'
import { FILTER_FIELD_KDS_SYNC_LOG } from './kdsSyncLogFilterForm'
import KdsSyncLogDetailModal, {
  KdsSyncLogDetail,
} from './kdsSyncLogDetailModal'
import { getColumns } from './utils'

type Props = {
  onFilterMeta?: (filterMeta: FilterMeta) => void
}

const KdsSyncLogTable = ({ onFilterMeta }: Props) => {
  const [logResponse, setLogResponse] = useState<any>({})
  const [page, setPage] = useState<number>(0)

  const [detailModal, setDetailModal] = useState<{
    isOpen: boolean
    loading: boolean
    log?: KdsSyncLogDetail
  }>({ isOpen: false, loading: false })

  const { getAllQueries } = useQuery()
  const { search } = useLocation()

  const handleView = async (id: string) => {
    setDetailModal({ isOpen: true, loading: true })
    try {
      const { data } = await fetchKdsSyncLogDetail(id)
      setDetailModal({ isOpen: true, loading: false, log: data })
    } catch (error) {
      setDetailModal({ isOpen: true, loading: false })
    }
  }

  const closeDetailModal = () => {
    setDetailModal({ isOpen: false, loading: false })
  }

  const columns = useMemo(
    () => getColumns({ page, onView: handleView }),
    [page],
  )

  const getLogs = async () => {
    try {
      const queries = getAllQueries()
      const { limit, page: pageParam } = queries
      const filters = filterEmptyValues(
        queries[FILTER_FIELD_KDS_SYNC_LOG] || {},
      )

      const { data } = await fetchKdsSyncLogs({
        limit: limit || 10,
        page: pageParam || 1,
        ...filters,
      })

      setPage(calculateOffset(data))
      setLogResponse(data)
      if (data?.filterMeta) {
        onFilterMeta?.(data.filterMeta)
      }
    } catch (error) {
      setLogResponse({ docs: [] })
    }
  }

  useEffect(() => {
    getLogs()
  }, [search])

  return (
    <>
      <DataTable columns={columns} rows={logResponse?.docs} identifier="_id" />

      <DataTablePagination
        currentPage={logResponse?.page}
        totalPages={logResponse?.totalPages}
        hasNextPage={logResponse?.hasNextPage}
        hasPrevPage={logResponse?.hasPrevPage}
        limit={logResponse?.limit}
        nextPage={logResponse?.nextPage}
        prevPage={logResponse?.prevPage}
      />

      <KdsSyncLogDetailModal
        isOpen={detailModal.isOpen}
        closeModal={closeDetailModal}
        log={detailModal.log}
        loading={detailModal.loading}
      />
    </>
  )
}

export default KdsSyncLogTable
